import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Bold, Italic, List, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { contentProjectsAPI } from '../../services/api';
import { POKindTag } from '../shared/POKindTag';

const NONE = '__none__';

const emptyForm = {
  name: '', description: '', ms_owner: '', pm_owner: '', target_delivery_date: '', linked_po_id: '', notes_html: '',
};

const cleanNotesHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  tmp.querySelectorAll('p').forEach(p => {
    if (p.querySelector('ul, ol')) {
      const parent = p.parentNode;
      while (p.firstChild) parent.insertBefore(p.firstChild, p);
      parent.removeChild(p);
    }
  });
  tmp.querySelectorAll('p').forEach(p => {
    if (!p.textContent.trim() && !p.querySelector('img, ul, ol, a')) p.remove();
  });
  return tmp.innerHTML;
};

export const ContentProjectFormDialog = ({
  open, onOpenChange, initial, msOwnerOptions = [], pmOwnerOptions = [], pos = [], currentUser, onSaved, onCreated, assignCount = 0,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      const next = initial ? {
        name: initial.name || '',
        description: initial.description || '',
        ms_owner: initial.ms_owner || '',
        pm_owner: initial.pm_owner || '',
        target_delivery_date: initial.target_delivery_date || '',
        linked_po_id: initial.linked_po_id || '',
        notes_html: initial.notes_html || '',
      } : { ...emptyForm };
      setForm(next);
      setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = next.notes_html || ''; }, 0);
    }
  }, [open, initial]);

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); };
  const handleInsertLink = () => { const url = window.prompt('Enter URL'); if (url) exec('createLink', url); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        ms_owner: form.ms_owner || null,
        pm_owner: form.pm_owner || null,
        target_delivery_date: form.target_delivery_date || null,
        linked_po_id: form.linked_po_id || null,
        notes_html: cleanNotesHtml(editorRef.current?.innerHTML || ''),
      };
      if (isEdit) {
        await contentProjectsAPI.update(initial.id, payload);
        toast.success('Project updated');
      } else {
        const created = await contentProjectsAPI.create(payload, currentUser?.email || currentUser?.name || null);
        toast.success('Project created');
        onCreated && onCreated(created);
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save project');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="content-project-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Project' : 'New Project'}</DialogTitle>
          <DialogDescription>
            {!isEdit && assignCount > 0
              ? `${assignCount} selected item${assignCount !== 1 ? 's' : ''} will be added to this new project.`
              : 'Group related content items under one project so they can be tracked together.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Project Name</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. FY27 Security Content Program" data-testid="content-project-name-input" />
          </div>

          <div>
            <Label>Short Description</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="What this project covers..." rows={2} data-testid="content-project-description-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>MS Owner</Label>
              <Select value={form.ms_owner || NONE} onValueChange={v => setForm({ ...form, ms_owner: v === NONE ? '' : v })}>
                <SelectTrigger data-testid="content-project-msowner-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— None —</SelectItem>
                  {msOwnerOptions.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {msOwnerOptions.length === 0 && <p className="text-[11px] text-muted-foreground mt-1">Add MS Owners in Taxonomy settings.</p>}
            </div>
            <div>
              <Label>PM Owner</Label>
              <Select value={form.pm_owner || NONE} onValueChange={v => setForm({ ...form, pm_owner: v === NONE ? '' : v })}>
                <SelectTrigger data-testid="content-project-pmowner-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— None —</SelectItem>
                  {pmOwnerOptions.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {pmOwnerOptions.length === 0 && <p className="text-[11px] text-muted-foreground mt-1">Add PM Owners in Taxonomy settings.</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Target Delivery Date</Label>
              <Input type="date" value={form.target_delivery_date} onChange={e => setForm({ ...form, target_delivery_date: e.target.value })} data-testid="content-project-target-date-input" />
            </div>
            <div>
              <Label>PO Number</Label>
              <Select value={form.linked_po_id || NONE} onValueChange={v => setForm({ ...form, linked_po_id: v === NONE ? '' : v })}>
                <SelectTrigger data-testid="content-project-po-select"><SelectValue placeholder="Select PO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— No PO —</SelectItem>
                  {pos.map(p => <SelectItem key={p.id} value={p.id}><span className="flex items-center gap-2"><span>{p.display_label || p.po_number}</span><POKindTag kind={p.kind} /></span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <div className="border rounded-lg overflow-hidden">
              <div className="flex items-center gap-1 border-b bg-muted/40 px-2 py-1">
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} data-testid="project-notes-bold"><Bold className="w-4 h-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} data-testid="project-notes-italic"><Italic className="w-4 h-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')} data-testid="project-notes-list"><List className="w-4 h-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={handleInsertLink} data-testid="project-notes-link"><Link2 className="w-4 h-4" /></Button>
              </div>
              <div
                ref={editorRef}
                contentEditable
                className="rich-text-content min-h-[90px] px-3 py-2 text-sm focus:outline-none"
                data-testid="content-project-notes-editor"
                suppressContentEditableWarning
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="content-project-save-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : (assignCount > 0 ? `Create & Add ${assignCount}` : 'Create Project'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContentProjectFormDialog;
